<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import ListingPagination from '@/components/listing-pagination'
import i18n from '@/i18n'
import { mapState } from 'vuex'
import { SESSION, SHARED } from '@/constants'
import { sessionHelper, ddlHelper } from '@/utils'
import { CONFIGURATION_MODULE } from '@/state/modules/configuration'
import { configurationMethods } from '@/state/helpers'

export default {
  page: { title: i18n.t('label.listingX', [i18n.t('module.configuration')]) },
  components: { Layout, PageHeader, MultiSelect, ListingPagination },
  data() {
    return {
      title: i18n.t('label.listingX', [i18n.t('module.configuration')]),
      breadcrumbItems: [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.configuration'), active: true },
      ],
      valueTypeDdl: ddlHelper.getConfigurationValueTypes(),
      filter: '',
      searchCriteria: {},
      showFilters: false,
      fields: [
        { key: 'label', sortable: false, label: i18n.t('label.key') },
        { key: 'value', sortable: false, label: i18n.t('label.value') },
        { key: 'valueType', sortable: false, label: i18n.t('configuration.valueType') },
        { key: 'updatedAt', sortable: false, label: i18n.t('label.lastUpdated') },
        { key: 'action', sortable: false, label: i18n.t('label.action') },
      ],
    }
  },
  computed: {
    ...mapState(CONFIGURATION_MODULE, {
      tableRows: (state) => state.list.data,
      pagination: (state) => state.list.pagination,
      status: (state) => state.status,
    }),
    activeFilterCount() {
      return [this.searchCriteria.valueTypes].filter((v) => Array.isArray(v) && v.length > 0).length
    },
  },
  created() {
    this.initSearchCriteria()
  },
  methods: {
    ...configurationMethods,

    initSearchCriteria() {
      if (sessionHelper.hasSearchCriteria(SESSION.CONFIGURATIONS)) {
        this.searchCriteria = sessionHelper.retrieveSearch(SESSION.CONFIGURATIONS)
        this.filter = (this.searchCriteria.searchArray || [])[0] || ''
        this.showFilters = this.activeFilterCount > 0
      } else {
        this.searchCriteria = this.defaultCriteria()
      }

      this.getList(this.searchCriteria)
    },

    defaultCriteria() {
      return {
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        pageSize: SHARED.DEFAULT_PAGE_SIZE,
        searchArray: [],
        valueTypes: [],
      }
    },

    getList(filter) {
      this.getAllConfiguration({ filter })
      this.searchCriteria = filter
      sessionHelper.storeSearch(SESSION.CONFIGURATIONS, filter)
    },

    pageNavigation(pageNumber) {
      this.getList({ ...this.searchCriteria, pageNumber })
    },

    searchListing() {
      this.getList({
        ...this.searchCriteria,
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        searchArray: this.filter ? [this.filter] : [],
      })
    },

    resetFilters() {
      this.filter = ''
      this.getList(this.defaultCriteria())
    },

    /**
     * A long value is truncated in the table, not wrapped: several of these are
     * URLs and one of them would otherwise make every row three lines tall.
     */
    displayValue(row) {
      if (row.value === null || row.value === '') return '—'

      const text = String(row.value)
      return text.length > 60 ? text.slice(0, 60) + '…' : text
    },

    goEdit(row) {
      this.$router.push({ name: 'Configuration-update', params: { key: row.key } })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <b-alert show variant="warning" class="small d-flex align-items-start">
          <i class="mdi mdi-alert-outline font-size-16 mr-2"></i>
          <span>{{ $t('message.configurationWarning') }}</span>
        </b-alert>

        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center w-100">
                  {{ $t('label.search') }}
                  <b-form-input
                    v-model="filter"
                    type="search"
                    :placeholder="$t('label.search')"
                    class="form-control form-control-sm ml-2"
                    @change="searchListing"
                  ></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button variant="outline-secondary" size="sm" @click="showFilters = !showFilters">
                  <i class="mdi mdi-filter-variant mr-1"></i>{{ $t('label.filter') }}
                  <b-badge v-if="activeFilterCount" variant="primary" class="ml-1">{{ activeFilterCount }}</b-badge>
                </b-button>
                <!-- No "create": a key only means something if some PHP reads
                     it, so new keys arrive with the code that reads them. -->
              </div>
            </div>

            <b-collapse v-model="showFilters">
              <div class="border rounded p-3 mb-3 bg-light">
                <b-row>
                  <b-col cols="12" md="6" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('configuration.valueType') }}</label>
                    <MultiSelect
                      v-model="searchCriteria.valueTypes"
                      :options="valueTypeDdl"
                      @change="searchListing"
                    />
                  </b-col>
                  <b-col cols="12" class="text-right">
                    <b-button variant="link" size="sm" @click="resetFilters">
                      {{ $t('label.clearFilters') }}
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </b-collapse>

            <div class="table-responsive mb-0">
              <b-table :items="tableRows" :fields="fields" responsive="sm" :busy="!status.list" show-empty>
                <template #empty>
                  <p class="text-muted text-center my-3">{{ $t('label.noRecordFound') }}</p>
                </template>

                <template #cell(label)="data">
                  {{ data.item.label }}
                  <!-- The raw key, because that is what appears in the code
                       and in a support request. -->
                  <code class="d-block text-muted font-size-11">{{ data.item.key }}</code>
                </template>

                <template #cell(value)="data">
                  <span class="small">{{ displayValue(data.item) }}</span>
                </template>

                <template #cell(valueType)="data">
                  <b-badge variant="light" class="border">{{ data.item.valueType }}</b-badge>
                </template>

                <template #cell(updatedAt)="data">
                  <span class="small text-muted">{{ data.item.updatedAt }}</span>
                </template>

                <template #cell(action)="data">
                  <a
                    v-if="$can('Update Configuration')"
                    href="javascript:void(0);"
                    class="text-info"
                    v-b-tooltip.hover
                    :title="$t('label.updateX', [$t('module.configuration')])"
                    @click="goEdit(data.item)"
                  >
                    <i class="mdi mdi-pencil font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>

            <ListingPagination :pagination="pagination" @change="pageNavigation" />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
